import { z } from "zod"

import { organizationSlugSchema } from "@/lib/organizationSlug"
import { getOrganizationBearerHeaders } from "@/utils/auth/organization-token"

import { notebookSchema } from "./notebooksApi"

export const SHARING_AUDIENCES = [
  "private",
  "workspace",
  "organization",
  "public",
] as const
export type SharingAudience = (typeof SHARING_AUDIENCES)[number]
const sharingSchema = z.object({
  audience: z.enum(SHARING_AUDIENCES),
  token: z.string().nullable(),
})
export type NotebookSharing = z.infer<typeof sharingSchema>
export interface NotebookSharingScope {
  notebookId: string
  organizationSlug: string
  workspaceId: string
}

// Keep reloads behind an in-flight write, including after the dialog unmounts.
const pendingSharingWrites = new Map<string, Promise<NotebookSharing>>()

export async function notebookSharingRequest(
  scope: NotebookSharingScope,
  audience?: SharingAudience,
): Promise<NotebookSharing> {
  const key = JSON.stringify([
    scope.organizationSlug,
    scope.workspaceId,
    scope.notebookId,
  ])
  const previous = pendingSharingWrites.get(key)
  const request = (async () => {
    await previous?.catch(() => undefined)
    return sendNotebookSharingRequest(scope, audience)
  })()
  if (audience) {
    pendingSharingWrites.set(key, request)
  }
  try {
    return await request
  } finally {
    if (pendingSharingWrites.get(key) === request) {
      pendingSharingWrites.delete(key)
    }
  }
}

async function sendNotebookSharingRequest(
  scope: NotebookSharingScope,
  audience?: SharingAudience,
): Promise<NotebookSharing> {
  const headers = await getOrganizationBearerHeaders(scope.organizationSlug)
  const query = new URLSearchParams({
    organization: scope.organizationSlug,
    workspace: scope.workspaceId,
  })
  const response = await fetch(
    `/api/sofia/notebooks/${encodeURIComponent(scope.notebookId)}/sharing?${query}`,
    {
      method: audience ? "PATCH" : "GET",
      cache: "no-store",
      headers: { ...headers, "Content-Type": "application/json" },
      ...(audience
        ? {
            body: JSON.stringify({
              organization: scope.organizationSlug,
              workspace: scope.workspaceId,
              audience,
            }),
          }
        : {}),
    },
  )
  if (!response.ok) {
    throw new Error("Could not update or load sharing. Please try again.")
  }
  return z.object({ sharing: sharingSchema }).parse(await response.json())
    .sharing
}

/** The capability stays in the fragment, outside HTTP request URLs and referrers. */
export function notebookSharePath(
  scope: NotebookSharingScope,
  sharing: NotebookSharing,
): string | null {
  if (sharing.audience === "private" || !sharing.token) {
    return null
  }
  const fragment = new URLSearchParams({ token: sharing.token })
  if (sharing.audience !== "public") {
    fragment.set("organization", scope.organizationSlug)
  }
  if (sharing.audience === "workspace") {
    fragment.set("workspace", scope.workspaceId)
  }
  return `/shared/notebooks/${encodeURIComponent(scope.notebookId)}#${fragment}`
}

export function parseNotebookShareFragment(fragment: string) {
  const params = new URLSearchParams(fragment.replace(/^#/, ""))
  return z
    .object({
      token: z.string().regex(/^[a-f0-9]{64}$/),
      organization: organizationSlugSchema.nullable(),
      workspace: z.string().min(1).max(200).nullable(),
    })
    .parse({
      token: params.get("token"),
      organization: params.get("organization"),
      workspace: params.get("workspace"),
    })
}

export async function getSharedNotebook(
  notebookId: string,
  fragment: string,
  signal?: AbortSignal,
) {
  const share = parseNotebookShareFragment(fragment)
  const query = new URLSearchParams()
  let headers: Record<string, string> = {
    "X-Coval-Notebook-Share-Token": share.token,
  }
  if (share.organization) {
    headers = {
      ...headers,
      ...(await getOrganizationBearerHeaders(share.organization)),
    }
    query.set("organization", share.organization)
    if (share.workspace) {
      query.set("workspace", share.workspace)
    }
  }
  const response = await fetch(
    `/api/sofia/shared-notebooks/${encodeURIComponent(notebookId)}?${query}`,
    {
      headers,
      signal,
      cache: "no-store",
      credentials: share.organization ? "same-origin" : "omit",
    },
  )
  if (!response.ok) {
    throw new Error(
      "This link is unavailable or you do not have access. For a workspace or organization link, sign in with an account that has access.",
    )
  }
  const data = z
    .object({ notebook: z.record(z.string(), z.unknown()) })
    .parse(await response.json())
  const source = z
    .object({
      organization_slug: organizationSlugSchema,
    })
    .parse(data.notebook)
  const notebook = notebookSchema.parse({
    ...data.notebook,
    created_by: null,
    cell_count: 0,
  })
  notebook.cell_count = notebook.snapshot.cells.length
  return { notebook, organizationSlug: source.organization_slug }
}
